import { ApolloQueryResult, useQuery } from '@apollo/client'
import { format } from 'date-fns'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import useAuth from 'data/gradoo/hooks/useAuth'
import { GET_PRODUCTION_EXPORTS } from 'data/layoutcreator/queries/production'
import { isExportLinkItemDisabledVar } from 'data/store/settings-export'
import { getDownloadPdfUrl } from 'screens/AdminOldProject/tabs/ExportTab/utils.'
import {
  GetProductionAttrsQuery,
  ProductionExportStatuses,
  ProductionExportType
} from 'shared/types/layoutcreator/graphql'
import Flex from 'shared/ui/Flex'
import Spinner from 'shared/ui/Spinner'

import { Table, Th, Tr } from './components/Table'
import Tag from './components/Tag'

const TABLE_HEADERS = [
  'type',
  'pages',
  'export.date',
  'export.by',
  'status'
]

const Dash = styled.span`
  color: ${({ theme }) => theme.color.base.c9};
`

const DownloadLink = styled.a`
  color: ${({ theme }) => theme.color.brand_02};
`

const FailedPagesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 12px;
`

const FailedPagesLabel = styled.span`
  color: ${({ theme }) => theme.color.base.c7};
`

const PageLink = styled.span`
  color: ${({ theme }) => theme.color.brand_02};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`

const EXPORTS_POLL_INTERVAL = 10_000

type HistoryProps = {
  refetchProductionAttrs: () => Promise<
    ApolloQueryResult<GetProductionAttrsQuery>
  >
  projectToken: string
}
const History = ({
  refetchProductionAttrs,
  projectToken
}: HistoryProps) => {
  const { formatMessage } = useIntl()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const projectId = pathname.split('/')[1]
  const { authGroupId } = useAuth()

  const {
    data: productionExports,
    loading: loadingProjectExports,
    stopPolling,
    startPolling
  } = useQuery(GET_PRODUCTION_EXPORTS, {
    variables: {
      groupId: authGroupId!,
      projectId
    },
    skip: !authGroupId,
    pollInterval: EXPORTS_POLL_INTERVAL
  })

  useEffect(() => {
    startPolling(EXPORTS_POLL_INTERVAL)

    if (
      productionExports?.getProductionExports?.length &&
      productionExports?.getProductionExports[0]?.status !==
        ProductionExportStatuses.InProgress
    ) {
      stopPolling()
      refetchProductionAttrs().then(() =>
        isExportLinkItemDisabledVar(false)
      )
    }

    return () => {
      stopPolling()
    }
  }, [
    productionExports?.getProductionExports,
    refetchProductionAttrs,
    startPolling,
    stopPolling
  ])

  return (
    <Table>
      <thead>
        <tr>
          {TABLE_HEADERS.map(id => (
            <Th key={id}>
              {formatMessage({
                id: `Project.export.history.th.${id}`
              })}
            </Th>
          ))}
          <Th>
            {loadingProjectExports ? (
              <Flex justifyContent="flex-end">
                <Spinner size={12} />
              </Flex>
            ) : null}
          </Th>
        </tr>
      </thead>
      <tbody>
        {productionExports?.getProductionExports.map(
          ({
            id,
            groupId,
            type,
            pages,
            startedAt,
            author,
            status,
            failedPages
          }) => (
            <Tr key={id}>
              <td>
                <Tag
                  textI18nId={`Project.export.history.type.${type}`}
                  intent={type}
                />
              </td>
              <td>{pages}</td>
              <td>
                {format(new Date(startedAt), 'dd.MM.yyyy – HH:mm')}
              </td>
              <td>{author?.fullName}</td>
              <td>
                <Tag
                  textI18nId={`Project.export.history.status.${status}`}
                  intent={status}
                />
                {failedPages && failedPages.length > 0 && (
                  <FailedPagesRow>
                    <FailedPagesLabel>
                      {formatMessage({
                        id: 'Project.export.history.failedPages'
                      })}
                    </FailedPagesLabel>
                    {failedPages.map((fp, index) => (
                      <span key={fp.pageId}>
                        <PageLink
                          title={
                            fp.error?.message || fp.error?.code || ''
                          }
                          onClick={() =>
                            navigate(
                              `/${projectId}/content/editor/${fp.pageId}`
                            )
                          }
                        >
                          {fp.pageNumber}
                        </PageLink>
                        {index < failedPages.length - 1 && ', '}
                      </span>
                    ))}
                  </FailedPagesRow>
                )}
                {failedPages && failedPages.length > 0 && (
                  <FailedPagesRow>
                    <FailedPagesLabel>
                      {formatMessage({
                        id: 'Project.export.history.failedPages'
                      })}
                    </FailedPagesLabel>
                    {failedPages.map((fp, index) => (
                      <span key={fp.pageId}>
                        <PageLink
                          title={
                            fp.error?.message || fp.error?.code || ''
                          }
                          onClick={() =>
                            navigate(
                              `/${projectId}/content/editor/${fp.pageId}`
                            )
                          }
                        >
                          {fp.pageNumber}
                        </PageLink>
                        {index < failedPages.length - 1 && ', '}
                      </span>
                    ))}
                  </FailedPagesRow>
                )}
              </td>
              <td>
                {(status === ProductionExportStatuses.Completed ||
                  status === ProductionExportStatuses.Warning) &&
                type === ProductionExportType.Preview &&
                projectToken ? (
                  <DownloadLink
                    href={getDownloadPdfUrl({
                      groupId,
                      projectId,
                      exportId: id,
                      token: projectToken
                    })}
                  >
                    {formatMessage({
                      id: 'Project.export.history.download'
                    })}
                  </DownloadLink>
                ) : (
                  <Dash>-</Dash>
                )}
              </td>
            </Tr>
          )
        )}
      </tbody>
    </Table>
  )
}

export default History
